/**
 * Открытие материала группами.
 *
 * Механизм один для всех режимов, которые ведут новичка от клавиши к
 * клавише: «Основы», «Цифры», «Пунктуация» и «Специальные символы». Каждый
 * из них показывает не весь набор сразу, а очередную группу — и следующая
 * ждёт, пока предыдущая не начнёт получаться.
 *
 * Пакет ничего не знает ни о раскладке, ни о том, что именно в группе:
 * состав группы — дело продуктовой функции, а здесь считается только
 * освоенность.
 */

import type { MasteryStat, MasteryUnitId } from '@type2x/shared-contracts';

/**
 * Порог освоенности, открывающий следующую группу.
 *
 * Клавиша, которая набирается без ошибок и без долгих пауз, получает оценку
 * около 0.95, а с одной ошибкой на десяток нажатий и задержкой около 600 мс —
 * примерно 0.85. Значение выбрано по фактическим значениям прогонов, а не на
 * глаз.
 *
 * Условие `isMastered` здесь не годится: оно требует трёх безошибочных сессий
 * подряд — это критерий интервального повторения, а не повод не показывать
 * новичку следующие клавиши.
 */
export const GROUP_MASTERY_THRESHOLD = 0.8;

/** Группа материала: идентификатор и учебные единицы, которые она открывает. */
export interface UnlockableGroup {
  readonly id: string;
  readonly unitIds: readonly MasteryUnitId[];
}

/**
 * Пройдена ли группа: каждая её единица набирается уверенно.
 *
 * Единица без попыток не пройдена: отсутствие статистики означает, что
 * пользователь этого ещё не видел, а не что у него получается.
 */
export function isGroupMastered(
  stats: Readonly<Record<string, MasteryStat>>,
  group: UnlockableGroup,
  threshold: number = GROUP_MASTERY_THRESHOLD,
): boolean {
  return group.unitIds.every((unitId) => {
    const stat = stats[unitId];
    return stat !== undefined && stat.attempts > 0 && stat.masteryScore >= threshold;
  });
}

/**
 * Группа, которую пользователь осваивает сейчас, либо null, если пройдены все.
 *
 * Это первая непройденная группа: пропускать вперёд нельзя — порядок групп
 * задаётся тем, что на что опирается.
 */
export function currentUnlockableGroup<TGroup extends UnlockableGroup>(
  groups: readonly TGroup[],
  stats: Readonly<Record<string, MasteryStat>>,
  threshold: number = GROUP_MASTERY_THRESHOLD,
): TGroup | null {
  return groups.find((group) => !isGroupMastered(stats, group, threshold)) ?? null;
}

/** Группы, пройденные к этому моменту. */
export function masteredGroups<TGroup extends UnlockableGroup>(
  groups: readonly TGroup[],
  stats: Readonly<Record<string, MasteryStat>>,
  threshold: number = GROUP_MASTERY_THRESHOLD,
): TGroup[] {
  return groups.filter((group) => isGroupMastered(stats, group, threshold));
}

/**
 * Группы, открытые упражнению: пройденные и текущая.
 *
 * Текущая входит в набор: упражнение существует ради неё, а пройденные
 * остаются, чтобы новое не тренировалось в отрыве от уже знакомого.
 */
export function unlockedGroups<TGroup extends UnlockableGroup>(
  groups: readonly TGroup[],
  stats: Readonly<Record<string, MasteryStat>>,
  threshold: number = GROUP_MASTERY_THRESHOLD,
): TGroup[] {
  const current = currentUnlockableGroup(groups, stats, threshold);
  if (current === null) return [...groups];

  const upTo = groups.findIndex((group) => group.id === current.id) + 1;
  return groups.slice(0, upTo);
}

/**
 * Наименьшая и наибольшая доля повторения среди сочетаний занятия.
 *
 * Повторение нужно всегда: символ, который перестали показывать, забывается.
 * Но и половину занятия оно занимать не должно — тогда новая группа
 * осваивается вдвое дольше.
 *
 * Правила сборки коротких сочетаний общие для всех режимов (ТЗ FR-EN-66):
 * один набор символов на сочетание, группа из одного символа дополняется, а
 * доля повторения решается освоенностью. Разные правила в «Основах», «Цифрах»
 * и пакетах означали бы, что одно и то же занятие собирается по-разному в
 * зависимости от того, откуда пришёл материал.
 */
export const MIN_REVIEW_SHARE = 0.1;
export const MAX_REVIEW_SHARE = 0.4;

/**
 * Какая доля занятия отводится повторению уже открытого.
 *
 * Зависит от того, насколько уверенно идут открытые единицы: постоянная доля
 * показывала бы забытую клавишу так же редко, как отскакивающую от зубов.
 * Группа открывается по порогу 0.8, поэтому только что пройденное просит
 * около десятой части занятия, а просевшее — до двух пятых.
 *
 * Общая для всех режимов, открывающих материал группами: две копии правила
 * означали бы, что «повторить» значит разное в соседних режимах
 * (ТЗ FR-EN-64).
 *
 * @param scores оценки освоенности открытых единиц, 0..1.
 */
export function reviewShare(scores: readonly number[]): number {
  if (scores.length === 0) return MAX_REVIEW_SHARE;

  const average = scores.reduce((total, score) => total + score, 0) / scores.length;
  return Math.min(Math.max(1 - average, MIN_REVIEW_SHARE), MAX_REVIEW_SHARE);
}
